import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField } from '@material-ui/core';
import Autocomplete from '@material-ui/lab-old/Autocomplete';
import { post } from 'services/request';
import ListIdsBlind from 'components/common/ListIdsBlind';
import { IRefIds } from 'constants/refs';

export type TRowItem = { translation_id: number; course_id: number; panel_id?: number };
export type TMemberRow = { pattern: string; items?: TRowItem[] };

type Props = {
  open: boolean;
  onClose: () => void;
  dialectId: number;
  row: TMemberRow;
  patterns: string[];
  onSuccess?: (changedIds: number[], editedPattern: string) => void;
};

const TranslationsPatternChangeModal: React.FC<Props> = ({ open, onClose, dialectId, row, patterns, onSuccess }) => {
  const [selected, setSelected] = useState<string>(row?.pattern || '');
  const [edited, setEdited] = useState<string>(row?.pattern || '');
  const [submitting, setSubmitting] = useState(false);
  const [errorText, setErrorText] = useState<string | null>(null);
  const [failedByCourse, setFailedByCourse] = useState<Record<number, number[]>>({});

  const translationIds = useMemo<number[]>(() => {
    return Array.isArray(row?.items) ? row.items.map((i) => i.translation_id) : [];
  }, [row]);

  const courseByTid = useMemo<Record<number, number>>(() => {
    const map: Record<number, number> = {};
    if (Array.isArray(row?.items)) {
      for (const it of row.items) {
        map[it.translation_id] = it.course_id || 0;
      }
    }
    return map;
  }, [row]);

  const panelByTid = useMemo<Record<number, number>>(() => {
    const map: Record<number, number> = {};
    if (Array.isArray(row?.items)) {
      for (const it of row.items) {
        if (typeof it.translation_id === 'number' && typeof it.panel_id === 'number') {
          map[it.translation_id] = it.panel_id || 0;
        }
      }
    }
    return map;
  }, [row]);

  // selection handled by Autocomplete onChange

  const handleClose = () => {
    setErrorText(null);
    setFailedByCourse({});
    onClose();
  };

  const handleSubmit = async () => {
    if (!dialectId || translationIds.length === 0) return;
    setSubmitting(true);
    try {
      const resp = await post<any>('translations/pattern/apply-change', {
        dialect_id: dialectId,
        translation_ids: translationIds,
        old_pattern: row?.pattern,
        new_pattern: edited,
      });
      const data: any = resp?.data;
      const errors: string[] = [];
      const grouped: Record<number, number[]> = {};
      if (data?.status === 'error' && data?.message) {
        errors.push(String(data.message));
      }
      if (Array.isArray(data?.results)) {
        for (const r of data.results) {
          if (r?.status === 'error') {
            const tid: number | undefined = r?.translation_id;
            const msg = r?.message || 'Unknown error';
            errors.push(`${typeof tid === 'number' ? `ID ${tid}` : 'item'}: ${msg}`);
            if (typeof tid === 'number') {
              const cid = courseByTid[tid] || 0;
              const pid = panelByTid[tid] || 0;
              if (!grouped[cid]) grouped[cid] = [];
              // Prefer panel id if available
              grouped[cid].push(pid > 0 ? pid : tid);
            }
          }
        }
      }
      if (errors.length > 0) {
        setErrorText(errors.join('\n'));
        setFailedByCourse(grouped);
      } else {
        if (typeof resp?.data === 'object' && Array.isArray(resp?.data?.results)) {
          const changedIds: number[] = [];
          for (const r of resp.data.results) {
            if (r?.status === 'ok' && typeof r?.translation_id === 'number') {
              changedIds.push(r.translation_id);
            }
          }
          onSuccess && onSuccess(changedIds, edited);
        }
        handleClose();
      }
    } catch (e) {
      console.error('Apply change failed', e);
      setErrorText('Failed to apply change. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>Change Pattern</DialogTitle>
      <DialogContent>
        {errorText ? (
          <div style={{ color: '#b91c1c', whiteSpace: 'pre-wrap', marginBottom: 8 }}>
            {errorText}
            {Object.keys(failedByCourse).length > 0 ? (
              <div style={{ marginTop: 6 }}>
                {Object.entries(failedByCourse).map(([cid, ids]) => (
                  <div key={cid} style={{ marginBottom: 4 }}>
                    <ListIdsBlind
                      refId={IRefIds.panels}
                      courseId={parseInt(cid, 10)}
                      ids={ids}
                      label={`open (course ${cid})`}
                    />
                  </div>
                ))}
              </div>
            ) : null}
          </div>
        ) : null}
        <AutoCompleteStyled
          options={patterns}
          value={selected}
          onChange={(event: any, newValue: string | null) => {
            const v = newValue || '';
            setSelected(v);
            setEdited(v);
          }}
          getOptionLabel={(option: string) => option}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Select pattern from group"
              variant="outlined"
              margin="dense"
              size="small"
              fullWidth
            />
          )}
          // keep dropdown simple; search-as-you-type built-in
        />
        <TextField
          label="Edit pattern"
          fullWidth
          margin="dense"
          value={edited}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEdited(e.target.value)}
          variant="outlined"
          size="small"
          multiline
          rows={3}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={submitting}>
          Cancel
        </Button>
        <Button color="primary" variant="contained" onClick={handleSubmit} disabled={submitting || !edited}>
          Change
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TranslationsPatternChangeModal;

const AutoCompleteStyled = styled(Autocomplete)`
  width: 100%;
  & .MuiInputBase-root {
    width: 100%;
  }
`;
